import { Select } from 'antd';
import { useMemo, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { updateUserTimezone } from '@/api/user';
import { IS_ADMIN_KEY } from '@/constants/roles';
import { useApiMutation } from '@/hooks/api';
import { useApiFeedback } from '@/hooks/useApiFeedback';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAppDispatch } from '@/store/hooks';
import { updateUser } from '@/store/userSlice';
import { getApiErrorMessage } from '@/utils/handleApiError';
import { getLocalStorageItem } from '@/utils/localStorage';
import { defaultFilterShowSearch } from '@/utils/selectHelpers';

const BROWSER_TIMEZONE = '';

const TimezoneSwitcher = () => {
  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const { showSuccess, showError, showWarning } = useApiFeedback();
  const { user } = useCurrentUser();
  const isAdminLoginAs = getLocalStorageItem(IS_ADMIN_KEY);

  const timezoneMutation = useApiMutation({
    mutationFn: (nextZone: string | null) => updateUserTimezone(nextZone),
    silent: true,
    onSuccess: (res) => {
      dispatch(updateUser({ default_timezone: res.default_timezone }));
      showSuccess('TIMEZONE_UPDATED');
    },
    onError: (error: unknown) => {
      showError(`${t('TIMEZONE_UPDATE_FAILED')}: ${getApiErrorMessage(error)}`);
    },
  });

  const options = useMemo(() => {
    const zones = Intl.supportedValuesOf('timeZone').map((zone) => ({
      value: zone,
      label: zone,
    }));
    return [
      { value: BROWSER_TIMEZONE, label: t('USE_BROWSER_TIMEZONE') },
      ...zones,
    ];
  }, [t]);

  const handleSelect = (nextZone: string) => {
    const stored = nextZone === BROWSER_TIMEZONE ? null : nextZone;
    if (isAdminLoginAs) {
      dispatch(updateUser({ default_timezone: stored }));
      showWarning('TIMEZONE_LOCAL_ONLY_ADMIN');
      return;
    }
    if (!user) {
      return;
    }
    timezoneMutation.mutate(stored);
  };

  const handleClick = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
  };

  return (
    <Select
      loading={timezoneMutation.isPending}
      disabled={timezoneMutation.isPending}
      showSearch={defaultFilterShowSearch}
      value={user?.default_timezone || BROWSER_TIMEZONE}
      options={options}
      onSelect={handleSelect}
      onClick={handleClick}
      data-testid="timezone-switcher"
    />
  );
};

export default TimezoneSwitcher;
